import { useEffect, useRef, useState } from 'react';
import useApi from '../../../hooks/useApi';
import {
  BulkActionResponse,
  BulkActionResult,
  ClaimProviderDetailResponse,
  ClaimProviderResponse,
  ClaimProviderReviewStatus,
  ClaimProviderUpdatePayload,
} from '../../../models/claimProvider';

type ClaimProviderFilters = {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  search?: string;
  claimed?: boolean | string;
  reviewStatus?: string;
  hasEmail?: boolean | string;
  hasInviteSent?: boolean | string;
  hasWebsite?: boolean | string;
  sportId?: string;
  cityKey?: string;
  minRating?: number | string;
};

const DEFAULT_PARAMS: ClaimProviderFilters = {
  claimed: false,
  page: 1,
  limit: 20,
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

const useClaimProviders = (initialParams?: ClaimProviderFilters) => {
  const applyFiltersRef = useRef<ClaimProviderFilters>({
    ...DEFAULT_PARAMS,
    ...initialParams,
  });
  const [errorMessage, setErrorMessage] = useState<string>();

  const { response, isLoading, launchCall, error } =
    useApi<ClaimProviderResponse>('admin/provider-discovery/providers');
  const { launchCall: launchAction } = useApi<
    BulkActionResponse | ClaimProviderDetailResponse | any
  >('admin/provider-discovery/providers');

  // Matches other admin list hooks: fetch once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    getClaimProviders();
  }, []);

  useEffect(() => {
    if (error) {
      setErrorMessage('Failed to load claim providers.');
    } else {
      setErrorMessage(undefined);
    }
  }, [error]);

  const getClaimProviders = (params?: ClaimProviderFilters) => {
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          delete (applyFiltersRef.current as Record<string, unknown>)[key];
        } else {
          (applyFiltersRef.current as Record<string, unknown>)[key] = value;
        }
      });
    }

    const queryParams = Object.entries(applyFiltersRef.current).reduce(
      (acc, [key, value]) => {
        if (value === undefined || value === null || value === '') {
          return acc;
        }
        acc[key] = value as string | number | boolean;
        return acc;
      },
      {} as Record<string, string | number | boolean>,
    );

    launchCall({ method: 'GET' }, queryParams);
  };

  const applyFilters = (
    filters: ClaimProviderFilters,
    reset?: boolean,
  ) => {
    if (reset) {
      applyFiltersRef.current = { ...DEFAULT_PARAMS, ...initialParams };
    }
    getClaimProviders({
      ...filters,
      page: 1,
    });
  };

  const handlePagination = (page: number, limit: number) => {
    getClaimProviders({ page, limit });
  };

  const nextPage = () => {
    const next = (response?.meta.currentPage || 1) + 1;
    const totalPages =
      Math.ceil((response?.meta?.total || 0) / (response?.meta?.limit || 20)) ||
      0;
    if (next <= totalPages) {
      handlePagination(next, response?.meta.limit || 20);
    }
  };

  const prevPage = () => {
    const prev = (response?.meta.currentPage || 1) - 1;
    if (prev > 0) {
      handlePagination(prev, response?.meta.limit || 20);
    }
  };

  const sendInvite = async (id: number | string) => {
    const result = await launchAction(
      { method: 'POST' },
      undefined,
      `admin/provider-discovery/providers/${id}/send-invite`,
    );
    getClaimProviders();
    return result;
  };

  const bulkSendInvites = async (
    ids: Array<number | string>,
  ): Promise<BulkActionResult | undefined> => {
    const result = (await launchAction(
      {
        method: 'POST',
        body: JSON.stringify({
          providerIds: ids.map(id => Number(id)),
        }),
      },
      undefined,
      'admin/provider-discovery/providers/send-invites',
    )) as BulkActionResponse | undefined;
    getClaimProviders();
    return result?.data;
  };

  const setReviewStatus = async (
    id: number | string,
    reviewStatus: ClaimProviderReviewStatus,
  ) => {
    const result = await launchAction(
      {
        method: 'PATCH',
        body: JSON.stringify({ reviewStatus }),
      },
      undefined,
      `admin/provider-discovery/providers/${id}/review-status`,
    );
    getClaimProviders();
    return result;
  };

  const bulkSetReviewStatus = async (
    ids: Array<number | string>,
    reviewStatus: ClaimProviderReviewStatus,
  ): Promise<BulkActionResult | undefined> => {
    const result = (await launchAction(
      {
        method: 'PATCH',
        body: JSON.stringify({
          providerIds: ids.map(id => Number(id)),
          reviewStatus,
        }),
      },
      undefined,
      'admin/provider-discovery/providers/review-status',
    )) as BulkActionResponse | undefined;
    getClaimProviders();
    return result?.data;
  };

  const updateProvider = async (
    id: number | string,
    payload: ClaimProviderUpdatePayload,
  ) => {
    const result = await launchAction(
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
      undefined,
      `admin/provider-discovery/providers/${id}`,
    );
    return result;
  };

  const getProviderById = async (id: number | string) => {
    const result = (await launchAction(
      { method: 'GET' },
      undefined,
      `admin/provider-discovery/providers/${id}`,
    )) as ClaimProviderDetailResponse | undefined;
    return result?.data;
  };

  return {
    getClaimProviders,
    applyFilters,
    nextPage,
    prevPage,
    sendInvite,
    bulkSendInvites,
    setReviewStatus,
    bulkSetReviewStatus,
    updateProvider,
    getProviderById,
    providers: response?.data,
    totalPages:
      Math.ceil((response?.meta?.total || 0) / (response?.meta?.limit || 20)) ||
      0,
    currentPage: response?.meta?.currentPage || 0,
    isLoading,
    error: errorMessage,
  };
};

export default useClaimProviders;
